import {
  HStack,
  Heading,
  Text,
  Container,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  useColorModeValue,
  Button,
  FormControl,
  FormLabel,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Table,
  Tbody,
  Td,
  Tr,
  Input,
  Collapse,
  useDisclosure,
  TableContainer,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  Spinner,
  Tag,
  VStack,
  Textarea,
  AspectRatio,
  Image,
} from '@chakra-ui/react'
import {
  useContractReads,
  useBalance,
  useAccount,
  useContractWrite,
  usePrepareContractWrite,
  useWaitForTransaction,
} from 'wagmi'
import networkMapping from '../assets/constants/network-mapping.json'
import subdao_ABI from '../assets/constants/SubDAO.json'
import hgManager_ABI from '../assets/constants/HeadgearManager.json'
import { ethers } from 'ethers'
import { useEffect, useState } from 'react'
import { getABIEtherscan, shortAddress, timeConverter } from '../utils/basicUtils'
import { TDaoInfo, TProposal, TVoteProposal, ProposalType } from '../types/SubDaoDetails.type'
// import { Filelike, Web3Storage, getFilesFromPath } from 'web3.storage'
import { NFTStorage } from 'nft.storage'
import { TokenInput } from 'nft.storage/dist/src/token'
import { motion } from 'framer-motion'
import { getLoreDetails } from '../utils/getLoreDetails'

const daoContract = {
  address: networkMapping['43113'].NeophyhteDAO as `0x${string}`,
  abi: subdao_ABI,
}

const hgManagerContract = {
  address: networkMapping['43113'].HeadgearManager as `0x${string}`,
  abi: hgManager_ABI,
}

const nftStorageClient = new NFTStorage({ token: process.env.REACT_APP_NFT_STORAGE_API_KEY! })

const NeoSubDao = () => {
  const { isOpen, onToggle } = useDisclosure()
  const { address } = useAccount()
  const [daoInfo, setDaoInfo] = useState<TDaoInfo>({} as TDaoInfo)
  const [userTokens, setUserTokens] = useState('')
  const [proposals, setProposals] = useState<TProposal[]>([] as TProposal[])
  const [voteProposal, setVoteProposal] = useState<TVoteProposal>({} as TVoteProposal)
  const [newMintPrice, setNewMintPrice] = useState<string>('0')
  const [callParams, setCallParams] = useState({ address: '', payableAmount: '', funcName: '', funcParams: '' })
  const [callArgs, setCallArgs] = useState<any[]>()
  const [currentProposal, setCurrentProposal] = useState(0)
  const [lore, setLore] = useState<TokenInput>({} as TokenInput)
  const [file, setFile] = useState(null)

  const { data } = useBalance({
    address: networkMapping['43113'].NeophyhteDAO as `0x${string}`,
    formatUnits: 'ether',
  })

  useContractReads({
    contracts: [
      {
        ...daoContract,
        functionName: 'symbol',
      },
      {
        ...daoContract,
        functionName: 'LORE',
      },
      {
        ...daoContract,
        functionName: 'QUORUM',
      },
      {
        ...daoContract,
        functionName: 'VOTING_PERIOD',
      },
      {
        ...daoContract,
        functionName: 'votingMemberCount',
      },
      {
        ...daoContract,
        functionName: 'proposalCount',
      },
      {
        ...daoContract,
        functionName: 'balanceOf',
        args: [address],
      },
      {
        ...hgManagerContract,
        functionName: 'subDaoDetails',
        args: [1],
      },
      {
        ...daoContract,
        functionName: 'totalSupply',
      },
    ],
    staleTime: 2_000,
    onSuccess(data: any) {
      console.log('Success', data)
      setDaoInfo({
        Symbol: data[0].toString(),
        Lore: data[1] || 'No Lore Set',
        Quorum: `Min ${Number(ethers.utils.formatUnits(data[2], 0))} %`,
        'Voting Period': `${Number(ethers.utils.formatUnits(data[3], 0)) / 86400} days`, // conversion to days
        'Total Members': Number(ethers.utils.formatUnits(data[4], 0)),
        'Total Proposals': Number(ethers.utils.formatUnits(data[5], 0)),
        'Mint Price': ethers.utils.formatUnits(data[7][1], 'ether') + ' Avax',
        'Total Supply': ethers.utils.formatUnits(data[8], 'ether') + ' Avax',
      })
      setUserTokens(ethers.utils.formatUnits(data[6], 'ether'))
      setNewMintPrice(ethers.utils.formatUnits(data[7][1], 'ether'))
    },
  })

  function getProposalDetails(): any[] {
    let contractReadArray = []
    for (let i = 1; i <= daoInfo['Total Proposals']; i++) {
      contractReadArray.push({
        ...daoContract,
        functionName: 'proposals',
        args: [i],
      })
      contractReadArray.push({
        ...daoContract,
        functionName: 'getProposalArrays',
        args: [i],
      })
    }
    return contractReadArray
  }

  useContractReads({
    contracts: getProposalDetails(),
    enabled: daoInfo['Total Proposals'] > 0,
    onSuccess(data) {
      console.log(data)
      let proposalList: TProposal[] = []
      data.forEach((prpsl: any, index) => {
        if (index % 2 === 0) {
          proposalList.push({
            yesVotes: prpsl[5],
            noVotes: prpsl[6],
            totalVotes: prpsl[5] + prpsl[6],
            creationTime: prpsl[7],
            proposalType: ProposalType[prpsl[0]],
            proposer: prpsl[8],
            accounts: [],
            amounts: [],
            payloads: [],
          })
        } else {
          let prpl: TProposal = proposalList.pop() as TProposal
          let x: TProposal = {
            ...prpl,
            accounts: prpsl[0].map((ppl: any) => ppl.toString()),
            amounts: prpsl[1].map((ppl: any) => ppl.toString()),
            payloads: prpsl[2].map((ppl: any) => ppl.toString()),
          }
          proposalList.push(x)
        }
      })
      setProposals(proposalList)
    },
  })

  const { config: voteConfig } = usePrepareContractWrite({
    address: networkMapping['43113'].NeophyhteDAO as `0x${string}`,
    abi: subdao_ABI,
    functionName: 'vote',
    args: [voteProposal.proposalId, voteProposal.decision],
    // enabled: voteProposal.proposalId > 0 && typeof voteProposal.decision === typeof Boolean,
  })

  const { data: voteData, write: voteOnProposal, error: voteError } = useContractWrite(voteConfig)

  const { isLoading: isVoting } = useWaitForTransaction({
    hash: voteData?.hash,
    onSuccess(data) {
      // setReveal(true)
      console.log('Mint Trx: ', data)
      // const tokenId = Number(ethers.utils.formatUnits(data.logs[0].topics[3], 0))
    },
  })

  const submitVote = (proposalId: number, decision: boolean): any => {
    setVoteProposal({ proposalId: proposalId, decision: decision })
    voteOnProposal?.()
  }

  const calTargetFunctionData = async () => {
    const testERC20abi = await getABIEtherscan(callParams.address)
    const iface = new ethers.utils.Interface(testERC20abi)
    const paramsArray = callParams.funcParams.split(',').map((prm) => prm.toString().trim())
    const targetFunctionData = iface.encodeFunctionData(callParams.funcName, [...paramsArray])
    return targetFunctionData
  }

  const proposalWriteArguments = async (type: number) => {
    if (type === 2) {
      const args = [
        2,
        [callParams.address],
        [ethers.utils.parseEther(callParams.payableAmount)],
        [await calTargetFunctionData()],
      ]
      setCallArgs(args)
    }
    if (type === 5) {
      const loreString = await loreIPFSUpload()
      // const byteString = fromIPFSCID(loreString)
      // TODO: Byte String Creation Pending
      const byteString = ethers.utils.toUtf8Bytes(loreString)
      const args = [5, [address], ['0'], [byteString]]
      setCallArgs(args)
    }
  }

  const { config: submitProposalConfig, error: submitPrepareError } = usePrepareContractWrite({
    address: networkMapping['43113'].NeophyhteDAO as `0x${string}`,
    abi: subdao_ABI,
    functionName: 'propose',
    args: callArgs,
  })

  const {
    data: submitProposalData,
    write: submitProposal,
    error: submitProposalError,
  } = useContractWrite(submitProposalConfig)

  const { isLoading: isProposalSubmitting } = useWaitForTransaction({
    hash: submitProposalData?.hash,
    onSettled(data, error) {
      // setReveal(true)
      console.log('Data or Error', { data, error })
      console.log('Proposal Submitted Trx Data: ', data)
      // const tokenId = Number(ethers.utils.formatUnits(data.logs[0].topics[3], 0))
    },
  })

  const setupNSubmitProposal = async (type: number) => {
    await proposalWriteArguments(type)
    submitProposal?.()
  }

  function getOwnershipPercent(): number {
    let totalSupply = parseFloat(daoInfo['Total Supply']?.split(' ')[0])
    let userTokenCount = userTokens ? parseFloat(userTokens) : 0
    const ownershipPercentage = (userTokenCount / totalSupply) * 100
    return ownershipPercentage
  }

  // Process Proposal
  const { config: processProposalConfig } = usePrepareContractWrite({
    address: networkMapping['43113'].NeophyhteDAO as `0x${string}`,
    abi: subdao_ABI,
    functionName: 'processProposal',
    args: [currentProposal],
  })

  const {
    data: processProposalData,
    write: processProposal,
    error: processProposalError,
  } = useContractWrite(processProposalConfig)

  const { isLoading: isProposalProcessing } = useWaitForTransaction({
    hash: submitProposalData?.hash,
    onSuccess(data) {
      // setReveal(true)
      console.log('Proposal Submitted Trx Data: ', data)
      // const tokenId = Number(ethers.utils.formatUnits(data.logs[0].topics[3], 0))
    },
  })

  function processCurrentProposal(proposalId: number) {
    setCurrentProposal(proposalId)
    processProposal?.()
  }
  // End of Procss Proposal

  const loreIPFSUpload = async () => {
    if (!lore || !lore.name || !lore.description || !file) {
      throw new Error('Lore not defined')
    }
    const imageFile = file || new File([''], 'nft.png', { type: 'image/png' })
    const metadata = await nftStorageClient.store({
      name: lore.name,
      description: lore.description,
      image: imageFile,
    })
    console.log(JSON.stringify(metadata))
    const loreUrl = `https://dweb.link/ipfs/${metadata.ipnft}/metadata.json`
    console.log(loreUrl)
    return metadata.ipnft.toString()
  }

  const handleLoreTextChange = (loreText: any) => {
    setLore({ ...lore, name: 'lore', description: loreText })
  }

  const handleChange = (event: any) => {
    setFile(event.target.files[0])
  }

  return (
    <Box bg={useColorModeValue('gray.50', 'gray.800')} color={'gray.800'} minH="85vh">
      <Flex pb={12} pt={20} direction="column" justifyContent="space-evenly" alignItems="center">
        <Heading mb={6} color="primary.500" className={'btn-shine'}>
          Welcome to Neo DAO!
        </Heading>

        <VStack mb={6} wrap={'wrap'}>
          <Text fontSize={{ base: 'sm', md: 'xl' }} mx={6} textAlign={'center'}>
            DAO Ownership: {getOwnershipPercent()} %
          </Text>
          <Text fontSize={{ base: 'xs', md: 'md' }} mx={6} textAlign={'center'}>
            {`Token Holdings: ${userTokens} ${daoInfo.Symbol}`}
          </Text>
        </VStack>
        <Button onClick={onToggle}>Dao Stats</Button>
        <Collapse in={isOpen} animateOpacity>
          <TableContainer>
            <Table size="sm">
              <Tbody>
                {Object.keys(daoInfo).map((daoInfokey) => {
                  return (
                    <Tr>
                      <Td>{daoInfokey}</Td>
                      <Td>{daoInfo[daoInfokey as keyof TDaoInfo]}</Td>
                    </Tr>
                  )
                })}
                <Tr>
                  <Td>Tresury Balance</Td>
                  <Td>
                    {data?.formatted} {data?.symbol}
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>
        </Collapse>

        <Container>
          <Tabs size={'lg'} variant="solid-rounded" defaultIndex={0} align="center" pt={5}>
            <TabList mb="1em">
              <Tab
                whiteSpace={'nowrap'}
                border={'1px solid #48d7d4'}
                _selected={{
                  boxShadow: '3px 3px #48d7d4',
                }}
                me={'12px'}
              >
                Vote on Proposal
              </Tab>
              <Tab
                whiteSpace={'nowrap'}
                border={'1px solid #48d7d4'}
                _selected={{
                  boxShadow: '3px 3px #48d7d4',
                }}
                me={'12px'}
              >
                Create Proposal
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Stack
                  spacing={4}
                  w={'full'}
                  rounded={'xl'}
                  border={'1px solid #02c6c9'}
                  boxShadow={'5px 5px #02c6c9'}
                  p={6}
                  align={'center'}
                  mt={'20px'}
                >
                  <Heading textAlign={'left'}>Active Proposals</Heading>
                  <Accordion>
                    {proposals.map((prpsl, index) => {
                      return (
                        <AccordionItem>
                          <AccordionButton w={'full'}>
                            <Box as="span" flex="1" textAlign="left" minW={'320px'}>
                              Proposal {index + 1} <Tag mr={2}>{prpsl.proposalType}</Tag>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel pb={4}>
                            <Stack alignItems={'flex-start'}>
                              <Text>
                                {' '}
                                Creation: <Tag mr={2}>{timeConverter(prpsl.creationTime)} </Tag>
                              </Text>
                              <Text>
                                Proposer:
                                <Tag mr={2}>
                                  {prpsl.proposer === networkMapping['43113'].HeadgearManager
                                    ? 'HGManger'
                                    : shortAddress(prpsl.proposer)}
                                </Tag>
                              </Text>
                              {prpsl.proposalType === 'MEMBERSHIP' && (
                                <>
                                  <Text>
                                    For:
                                    <Tag mr={2}>{prpsl.accounts.map((act) => shortAddress(act)).toString()}</Tag>
                                  </Text>
                                </>
                              )}
                              {prpsl.proposalType === 'PRICE' && (
                                <Text>
                                  New Mint Price: <Tag mr={2}>{ethers.utils.formatEther(prpsl.amounts[0])} Avax</Tag>
                                </Text>
                              )}
                              {prpsl.proposalType === 'LORE' && <LoreDetails payload={prpsl.payloads[0].toString()} />}
                              {prpsl.totalVotes > 0 && (
                                <Stat>
                                  <StatLabel mb={'5px'}>Total Votes: {prpsl.totalVotes}</StatLabel>
                                  <StatNumber mb={'5px'}>
                                    <StatLabel>{`${((prpsl.yesVotes * 100) / prpsl.totalVotes).toFixed(
                                      2
                                    )}%`}</StatLabel>
                                    <Progress
                                      value={(prpsl.yesVotes * 100) / prpsl.totalVotes}
                                      size="lg"
                                      colorScheme="teal"
                                      borderRadius={'10px'}
                                    />
                                  </StatNumber>
                                  <StatNumber mb={'5px'}>
                                    <StatLabel>{`${((prpsl.noVotes * 100) / prpsl.totalVotes).toFixed(2)}%`}</StatLabel>
                                    <Progress
                                      value={(prpsl.noVotes * 100) / prpsl.totalVotes}
                                      size="lg"
                                      colorScheme="red"
                                      borderRadius={'10px'}
                                    />
                                  </StatNumber>
                                </Stat>
                              )}
                            </Stack>
                            <Text>{voteError?.message}</Text>
                            {isVoting ? (
                              <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
                            ) : (
                              <HStack justify={'space-evenly'} mt={'4'}>
                                <Button colorScheme="teal" onClick={(e) => submitVote(index + 1, true)}>
                                  Accept
                                </Button>
                                <Button colorScheme="red" onClick={(e) => submitVote(index + 1, false)}>
                                  Reject
                                </Button>
                              </HStack>
                            )}
                            {prpsl.totalVotes > 0 && (
                              <HStack justify={'space-evenly'} mt={'4'}>
                                <Button colorScheme="teal" onClick={(e) => processCurrentProposal(index + 1)}>
                                  Process
                                </Button>
                              </HStack>
                            )}
                          </AccordionPanel>
                        </AccordionItem>
                      )
                    })}
                  </Accordion>
                </Stack>
              </TabPanel>
              <TabPanel>
                <Stack
                  spacing={4}
                  w={'full'}
                  rounded={'xl'}
                  border={'1px solid #02c6c9'}
                  boxShadow={'5px 5px #02c6c9'}
                  p={6}
                  align={'center'}
                  mt={'20px'}
                >
                  <Heading textAlign={'left'}>Create Proposal</Heading>
                  <Accordion>
                    <AccordionItem>
                      <AccordionButton w={'full'}>
                        <Box as="span" flex="1" textAlign="left" minW={'320px'}>
                          Purchase NFT
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <form>
                          <FormControl>
                            <FormLabel> NFT Contract Address </FormLabel>
                            <Input
                              type="string"
                              value={callParams.address}
                              onChange={(e) => setCallParams({ ...callParams, address: e.target.value })}
                            ></Input>
                          </FormControl>
                          <FormControl>
                            <FormLabel> Mint Function Name </FormLabel>
                            <Input
                              type="string"
                              value={callParams.funcName}
                              onChange={(e) => setCallParams({ ...callParams, funcName: e.target.value })}
                            ></Input>
                          </FormControl>
                          <FormControl>
                            <FormLabel> Mint Function Parameters [comma seperated values]</FormLabel>
                            <Input
                              type="string"
                              value={callParams.funcParams}
                              onChange={(e) => setCallParams({ ...callParams, funcParams: e.target.value })}
                            ></Input>
                          </FormControl>
                          <FormControl>
                            <FormLabel> Avax Payment</FormLabel>
                            <Input
                              type="string"
                              value={callParams.payableAmount}
                              onChange={(e) => setCallParams({ ...callParams, payableAmount: e.target.value })}
                            ></Input>
                          </FormControl>
                          <p>{JSON.stringify(submitProposalError)}</p>
                          <HStack justify={'space-evenly'} mt={'4'}>
                            <Button colorScheme="teal" onClick={(e) => setupNSubmitProposal(2)}>
                              Submit Mint Proposal
                            </Button>
                          </HStack>
                        </form>
                      </AccordionPanel>
                    </AccordionItem>
                    <AccordionItem>
                      <AccordionButton w={'full'}>
                        <Box as="span" flex="1" textAlign="left" minW={'320px'}>
                          Create Lore Proposal
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <form>
                          <FormControl>
                            <FormLabel> Lore Image</FormLabel>
                            <AspectRatio width="84" ratio={4}>
                              <Box
                                borderColor="gray.300"
                                borderStyle="dashed"
                                borderWidth="2px"
                                rounded="md"
                                shadow="sm"
                                role="group"
                                transition="all 150ms ease-in-out"
                                _hover={{
                                  shadow: 'md',
                                }}
                                as={motion.div}
                                initial="rest"
                                animate="rest"
                                whileHover="hover"
                              >
                                <Box position="relative" width="100%">
                                  <Stack p="4" textAlign="center" spacing="1">
                                    <Heading fontSize="lg" color="gray.700" fontWeight="bold">
                                      Drag and Drop
                                    </Heading>
                                    <Text fontWeight="light">or click to upload</Text>
                                  </Stack>
                                  <Input
                                    type="file"
                                    width="100%"
                                    height={'32'}
                                    position="absolute"
                                    top="0"
                                    left="0"
                                    opacity="0"
                                    aria-hidden="true"
                                    accept="image/*"
                                    onChange={handleChange}
                                  />
                                </Box>
                              </Box>
                            </AspectRatio>
                            {file && (
                              <Stack>
                                <Text>File Name: {file['name']}</Text>
                              </Stack>
                            )}
                          </FormControl>
                          <FormControl>
                            <FormLabel> Lore Text</FormLabel>
                            <Textarea
                              value={lore.description}
                              onChange={(e) => handleLoreTextChange(e.target.value)}
                            ></Textarea>
                          </FormControl>
                          <HStack justify={'space-evenly'} mt={'4'}>
                            <Button colorScheme="teal" onClick={(e) => setupNSubmitProposal(5)}>
                              Submit Lore Proposal
                            </Button>
                          </HStack>
                        </form>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Stack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Container>
      </Flex>
    </Box>
  )
}

const LoreDetails = (payload: any) => {
  const [lore, setLore] = useState({ desc: '', imgUrl: '' })
  useEffect(() => {
    const fetchData = async (pload: any) => {
      const data = await getLoreDetails(pload.payload.toString())
      setLore({ desc: data.description, imgUrl: data.imageUrl })
    }
    if (payload) {
      fetchData(payload)
    }
  }, [])

  return (
    <HStack>
      <Image src={lore.imgUrl} width={'100px'} />
      <Text fontSize={'18px'}>{lore.desc}</Text>
    </HStack>
  )
}

export default NeoSubDao
